import React, { useEffect, useState } from "react";
import { AlertTriangle, KeyRound, Save, ShieldCheck, UploadCloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { ETipoPermissao } from "../../Enum/Enum";
import { useOperador } from "../../Hooks/useOperador";
import { assinaturaDigitalService } from "../../Services/assinaturaDigital";
import "./styles.css";

const formInicial = {
  certificado: null,
  senha: "",
  certificadoTitular: "",
  certificadoCnpj: "",
  certificadoSerial: "",
  validadeCertificado: "",
  observacao: "",
};

const formatarData = (valor) => {
  if (!valor) return "Nao informado";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return valor;
  return data.toLocaleDateString("pt-BR");
};

const AssinaturaDigitalCertificado = () => {
  const navigate = useNavigate();
  const { IDOperador, nome, permissaoPrincipal } = useOperador();
  const [certificadoAtual, setCertificadoAtual] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [loading, setLoading] = useState(false);
  const isAdmin = Number(permissaoPrincipal) === ETipoPermissao.ADMIN;

  const carregarCertificado = async () => {
    try {
      setLoading(true);
      const response = await assinaturaDigitalService.buscarCertificado();
      setCertificadoAtual(response);
    } catch (error) {
      console.error("Erro ao carregar certificado ICP-Brasil.", error);
      Swal.fire("Erro", "Nao foi possivel carregar o certificado.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate("/Menu");
      return;
    }

    carregarCertificado();
  }, [isAdmin, navigate]);

  const atualizarCampo = (campo, valor) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const salvar = async () => {
    if (!form.certificado) {
      Swal.fire("Atencao", "Selecione o arquivo do certificado.", "warning");
      return;
    }

    if (!form.senha) {
      Swal.fire("Atencao", "Informe a senha do certificado.", "warning");
      return;
    }

    try {
      setLoading(true);
      await assinaturaDigitalService.salvarCertificado(form);
      setForm(formInicial);
      await carregarCertificado();
      Swal.fire("Sucesso", "Certificado atualizado com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao salvar certificado ICP-Brasil.", error);
      Swal.fire(
        "Erro",
        error.response?.data?.errors?.default ||
          "Nao foi possivel salvar o certificado.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="menu-container-menu certificado-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell certificado-shell">
          <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />

          <section className="certificado-header">
            <div>
              <span><KeyRound size={17} /> ICP-Brasil</span>
              <h1>Certificado ICP-Brasil</h1>
            </div>
          </section>

          <section className="certificado-security">
            <ShieldCheck size={30} />
            <div>
              <h2>Seu certificado esta protegido.</h2>
              <p>
                Por seguranca, a senha e o arquivo do certificado nao ficam
                visiveis para os usuarios do sistema.
              </p>
              <small>
                Somente administradores autorizados podem substituir ou atualizar
                essas informacoes.
              </small>
            </div>
          </section>

          <section className="certificado-grid">
            <article className="certificado-status">
              <header>
                <h2>Status atual</h2>
                <span className={certificadoAtual?.configurado ? "is-ok" : "is-pending"}>
                  {certificadoAtual?.configurado ? "Configurado" : "Nao configurado"}
                </span>
              </header>

              {loading && <p>Carregando certificado...</p>}

              {!loading && certificadoAtual?.configurado && (
                <dl>
                  <div>
                    <dt>Titular</dt>
                    <dd>{certificadoAtual.certificadoTitular || "Nao informado"}</dd>
                  </div>
                  <div>
                    <dt>CNPJ</dt>
                    <dd>{certificadoAtual.certificadoCnpj || "Nao informado"}</dd>
                  </div>
                  <div>
                    <dt>Serial</dt>
                    <dd>{certificadoAtual.certificadoSerial || "Nao informado"}</dd>
                  </div>
                  <div>
                    <dt>Validade</dt>
                    <dd>{formatarData(certificadoAtual.validadeCertificado)}</dd>
                  </div>
                  <div>
                    <dt>Ultima atualizacao</dt>
                    <dd>{formatarData(certificadoAtual.dataCadastro)}</dd>
                  </div>
                </dl>
              )}

              {!loading && !certificadoAtual?.configurado && (
                <div className="certificado-empty">
                  <AlertTriangle size={22} />
                  <span>Nenhum certificado cadastrado para assinatura digital.</span>
                </div>
              )}
            </article>

            <article className="certificado-form">
              <header>
                <h2>Atualizar certificado</h2>
                <p>Informe o certificado e-CNPJ da Pentagrama.</p>
              </header>

              <label className="certificado-file">
                <UploadCloud size={24} />
                <span>
                  {form.certificado?.name || "Selecionar certificado .pfx ou .p12"}
                </span>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(event) =>
                    atualizarCampo("certificado", event.target.files?.[0] || null)
                  }
                />
              </label>

              <label>
                Senha do certificado
                <input
                  type="password"
                  value={form.senha}
                  onChange={(event) => atualizarCampo("senha", event.target.value)}
                  autoComplete="new-password"
                />
              </label>

              <div className="certificado-form__columns">
                <label>
                  Titular
                  <input
                    value={form.certificadoTitular}
                    onChange={(event) =>
                      atualizarCampo("certificadoTitular", event.target.value)
                    }
                    placeholder="Pentagrama..."
                  />
                </label>
                <label>
                  CNPJ
                  <input
                    value={form.certificadoCnpj}
                    onChange={(event) =>
                      atualizarCampo("certificadoCnpj", event.target.value)
                    }
                  />
                </label>
              </div>

              <div className="certificado-form__columns">
                <label>
                  Serial
                  <input
                    value={form.certificadoSerial}
                    onChange={(event) =>
                      atualizarCampo("certificadoSerial", event.target.value)
                    }
                  />
                </label>
                <label>
                  Validade
                  <input
                    type="date"
                    value={form.validadeCertificado}
                    onChange={(event) =>
                      atualizarCampo("validadeCertificado", event.target.value)
                    }
                  />
                </label>
              </div>

              <label>
                Observacao
                <textarea
                  rows={3}
                  value={form.observacao}
                  onChange={(event) =>
                    atualizarCampo("observacao", event.target.value)
                  }
                />
              </label>

              <footer>
                <button type="button" onClick={salvar} disabled={loading}>
                  <Save size={17} />
                  Salvar certificado
                </button>
              </footer>
            </article>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaDigitalCertificado;
